import React, { useMemo, useRef } from 'react';
import { Button, Modal, Space, Table, Tag, Tooltip, Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import {
  LIST_BILL_ATTACHMENTS,
  GET_BILL_ATTACHMENT_DOWNLOAD_URL,
} from '../../graphql/queries/settlement/billAttachmentQueries';
import {
  CONFIRM_BILL_ATTACHMENT_UPLOAD,
  DELETE_BILL_ATTACHMENT,
} from '../../graphql/mutations/settlement/billAttachmentMutations';
import { fileUpload } from '../../utils/storageUtils';
import { formatTimeWithTimezone } from '../../utils/timezoneFormatter';
import { getBillStatusLabel } from '../../utils/billI18n';

/** 单个附件字节数上限（20 MB），与后端 `SettlementAttachmentService.MAX_FILE_BYTES` 对齐 */
const MAX_FILE_BYTES = 20 * 1024 * 1024;
/** 同一账单未删附件数量上限，与后端 `SettlementAttachmentService.MAX_ATTACHMENTS_PER_RESOURCE` 对齐 */
const MAX_ATTACHMENTS = 5;
/** 允许上传的扩展名白名单（小写、不含点），必须与后端 `ALLOWED_EXTENSIONS` 保持一致 */
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'xls', 'xlsx', 'doc', 'docx', 'pdf'];
/**
 * 高状态账单：上传附件需二次确认。文案与业务需求一致：
 * "当前账单已流转至：**，是否确认提交附件"
 */
const CONFIRM_REQUIRED_STATUSES = new Set(['EXTERNAL_CONFIRMED', 'INVOICE_CONFIRMED', 'PAID']);

export interface BillAttachmentRow {
  id: string;
  billId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  uploaderId: string;
  uploaderName: string;
  createdAt: number;
  canDelete: boolean;
}

export interface BillAttachmentPanelProps {
  billId: string;
  /** 当前账单状态，用于判断高状态二次确认；只读视角可传空 */
  billStatus?: string | null;
  /** true 时隐藏上传/删除按钮，仅保留查看与下载 */
  readOnly?: boolean;
}

const formatSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const extractExtension = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return '';
  return fileName.substring(dot + 1).toLowerCase();
};

const BillAttachmentPanel: React.FC<BillAttachmentPanelProps> = ({ billId, billStatus, readOnly }) => {
  const apolloClient = useApolloClient();
  const uploadingRef = useRef(false);

  const { data, loading, refetch } = useQuery(LIST_BILL_ATTACHMENTS, {
    variables: { billId },
    fetchPolicy: 'cache-and-network',
  });

  const [confirmUpload, { loading: confirming }] = useMutation(CONFIRM_BILL_ATTACHMENT_UPLOAD);
  const [deleteAttachment] = useMutation(DELETE_BILL_ATTACHMENT);

  const rows: BillAttachmentRow[] = useMemo(
    () => data?.listBillAttachments ?? [],
    [data],
  );

  const uploadFlow = async (file: File): Promise<void> => {
    const ext = extractExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      message.error(`仅支持 ${ALLOWED_EXTENSIONS.join(' / ')} 格式`);
      return;
    }
    if (file.size <= 0) {
      message.error('不能上传空文件');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      message.error(`附件不能超过 ${MAX_FILE_BYTES / 1024 / 1024}MB`);
      return;
    }
    if (rows.length >= MAX_ATTACHMENTS) {
      message.error(`附件已达 ${MAX_ATTACHMENTS} 个上限`);
      return;
    }

    const needConfirm = !!billStatus && CONFIRM_REQUIRED_STATUSES.has(billStatus);
    const proceed = async () => {
      if (uploadingRef.current) return;
      uploadingRef.current = true;
      const hide = message.loading('上传中...', 0);
      try {
        const { objectKey } = await fileUpload(file, 'bill-attachment');
        await confirmUpload({
          variables: {
            billId,
            objectKey,
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type || 'application/octet-stream',
            confirmedFlow: needConfirm,
          },
        });
        message.success('上传成功');
        await refetch();
      } catch (e: any) {
        message.error(e?.message || '上传失败');
      } finally {
        hide();
        uploadingRef.current = false;
      }
    };

    if (needConfirm) {
      Modal.confirm({
        title: '提交附件确认',
        content: `当前账单已流转至：${getBillStatusLabel(billStatus)}，是否确认提交附件？`,
        okText: '确认提交',
        cancelText: '取消',
        onOk: proceed,
      });
      return;
    }
    await proceed();
  };

  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      uploadFlow(file);
      // 交给自定义上传流；始终阻止 antd 默认上传行为
      return false;
    },
    showUploadList: false,
    multiple: false,
    accept: '.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx,.pdf',
    disabled: readOnly || confirming,
  };

  const handleDownload = async (row: BillAttachmentRow) => {
    try {
      const { data: dl } = await apolloClient.query({
        query: GET_BILL_ATTACHMENT_DOWNLOAD_URL,
        variables: { attachmentId: row.id },
        fetchPolicy: 'no-cache',
      });
      const url = dl?.getBillAttachmentDownloadUrl;
      if (!url) {
        message.error('获取下载链接失败');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      message.error(e?.message || '下载失败');
    }
  };

  const handleDelete = (row: BillAttachmentRow) => {
    Modal.confirm({
      title: '删除附件',
      content: `确认删除附件「${row.fileName}」？删除后不可恢复。`,
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteAttachment({ variables: { attachmentId: row.id } });
          message.success('已删除');
          await refetch();
        } catch (e: any) {
          message.error(e?.message || '删除失败');
        }
      },
    });
  };

  const columns = [
    {
      title: '文件名',
      dataIndex: 'fileName',
      key: 'fileName',
      render: (name: string) => (
        <Tooltip title={name}>
          <span className="truncate inline-block max-w-[320px] align-bottom">{name}</span>
        </Tooltip>
      ),
    },
    {
      title: '大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 110,
      // GraphQL Long scalar 会序列化为 string，需先 Number 转换再走 formatSize 的分支判断
      render: (v: number | string) => formatSize(Number(v)),
    },
    {
      title: '上传人',
      dataIndex: 'uploaderName',
      key: 'uploaderName',
      width: 140,
    },
    {
      title: '上传时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      // GraphQL Long scalar 序列化为 string；new Date(字符串数字) 会按日期串解析返回 Invalid Date，
      // 后续 .toISOString() 抛 RangeError 直接把整个页面渲染打崩，必须先转 Number
      render: (ms: number | string) => {
        const n = typeof ms === 'string' ? Number(ms) : ms;
        return n && Number.isFinite(n) ? formatTimeWithTimezone(new Date(n).toISOString()) : '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: unknown, row: BillAttachmentRow) => (
        <Space size="small">
          <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(row)}>
            下载
          </Button>
          {!readOnly && row.canDelete && (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          附件数量 <Tag>{rows.length} / {MAX_ATTACHMENTS}</Tag>
          <span className="ml-2 text-xs text-gray-400">
            支持 {ALLOWED_EXTENSIONS.join('/')}，单文件最大 {MAX_FILE_BYTES / 1024 / 1024}MB
          </span>
        </div>
        {!readOnly && (
          <Upload {...uploadProps}>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              disabled={rows.length >= MAX_ATTACHMENTS || confirming}
            >
              上传附件
            </Button>
          </Upload>
        )}
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="small"
        pagination={false}
      />
    </div>
  );
};

export default BillAttachmentPanel;
